import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatError, isValidEmail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/accountRole";
import { Crosshair } from "@/components/Crosshair";
import { timeAgo } from "@/lib/constants";
import { Ticket, Plus, Trash2, Copy, Users, Mail, Send, X } from "lucide-react";
import { LoadFailed } from "@/components/LoadState";
import { toast } from "@/lib/toast";

export default function AdminInvites() {
  const { user, loading } = useAuth();
  const [invites, setInvites] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [seats, setSeats] = useState({ seats_used: 0, seats_total: 0, full: false });
  const [form, setForm] = useState({ label: "", code: "", max_uses: "" });
  const [busy, setBusy] = useState(false);
  const [sendingFor, setSendingFor] = useState(null);
  const [sendEmail, setSendEmail] = useState("");
  const [sendBusy, setSendBusy] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  const load = () => {
    setLoadErr(null);
    api.get("/admin/beta/invites").then(({ data }) => setInvites(data.invites)).catch((err) => setLoadErr(formatError(err?.response?.data?.detail) || "Couldn't load invites."));
    api.get("/admin/beta/waitlist").then(({ data }) => setWaitlist(data.waitlist)).catch(() => {});
    api.get("/beta/status").then(({ data }) => setSeats(data)).catch(() => {});
  };

  useEffect(() => {
    if (user && isAdminRole(user.role)) load();
  }, [user]);

  if (loading || user === null) return <div className="min-h-screen" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdminRole(user.role)) return <Navigate to="/feed" replace />;

  const create = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) { toast.error("Enter a label for this invite."); return; }
    setBusy(true);
    try {
      await api.post("/admin/beta/invites", {
        label: form.label.trim(),
        code: form.code.trim() || null,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
      });
      toast.success("Invite code created.");
      setForm({ label: "", code: "", max_uses: "" });
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this invite code?")) return;
    try { await api.delete(`/admin/beta/invites/${id}`); toast.success("Deleted."); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success(`Copied ${code}`); };

  const openSend = (id) => { setSendingFor(id); setSendEmail(""); };

  const sendInvite = async (id) => {
    if (!isValidEmail(sendEmail)) { toast.error("Enter a valid email address."); return; }
    setSendBusy(true);
    try {
      const { data } = await api.post(`/admin/beta/invites/${id}/send`, { email: sendEmail.trim() });
      if (data.sent) toast.success(`Invite emailed to ${data.email}`);
      else toast.warning("Email not sent — sending domain not verified yet. Code is still valid.");
      setSendingFor(null);
      setSendEmail("");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSendBusy(false); }
  };

  return (
    <div className="text-foreground">
      <div className="max-w-[1000px] mx-auto px-4 py-6">
        <div className="flex items-center gap-2">
          <Ticket className="w-6 h-6 text-volt" strokeWidth={1.6} />
          <h1 className="font-heading text-4xl font-bold uppercase tracking-tight" data-testid="invites-title">Beta Invites</h1>
        </div>
        <Crosshair className="my-5" />

        <div className="border border-sniper bg-surface p-4 mb-6 flex flex-wrap items-center gap-x-8 gap-y-2" data-testid="seat-cap">
          <div>
            <p className="font-mono text-[11px] uppercase text-secondary-foreground">Seats used</p>
            <p className="font-heading text-2xl">{seats.seats_used} <span className="text-muted-foreground text-lg">/ {seats.seats_total}</span></p>
          </div>
          <div className={`font-mono text-xs uppercase px-2 py-1 border ${seats.full ? "border-blaze text-blaze" : "border-olive text-olive-bright"}`}>
            {seats.full ? "Beta full — waitlist active" : "Seats available"}
          </div>
        </div>

        <form onSubmit={create} className="border border-sniper bg-surface p-4 mb-6 grid sm:grid-cols-4 gap-3 items-end" data-testid="invite-form">
          <div className="sm:col-span-2">
            <label className="font-mono text-xs uppercase text-secondary-foreground">Label</label>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="invite-label"
              placeholder="e.g. Reddit r/longrange" className="mt-1 w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground transition-colors" />
          </div>
          <div>
            <label className="font-mono text-xs uppercase text-secondary-foreground">Code (optional)</label>
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="invite-code"
              placeholder="auto" className="mt-1 w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground uppercase transition-colors" />
          </div>
          <div>
            <label className="font-mono text-xs uppercase text-secondary-foreground">Max uses</label>
            <input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="invite-max-uses"
              placeholder="∞" className="mt-1 w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground transition-colors" />
          </div>
          <button disabled={busy} data-testid="invite-create"
            className="sm:col-span-4 sm:w-auto inline-flex items-center justify-center gap-1.5 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase px-5 py-2.5 transition-colors">
            <Plus className="w-4 h-4" /> Create Invite
          </button>
        </form>

        <div className="space-y-2 mb-8" data-testid="invite-list">
          {loadErr ? <LoadFailed reason={loadErr} onRetry={load} testid="invites-load-failed" />
           : invites.length === 0 ? <p className="text-sm font-body text-muted-foreground border border-sniper bg-surface p-6">No invite codes yet.</p> : null}
          {!loadErr && invites.map((inv) => (
            <div key={inv.invite_id} className="border border-sniper bg-surface p-3 flex flex-col gap-3" data-testid={`invite-${inv.invite_id}`}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <button onClick={() => copy(inv.code)} data-testid={`invite-copy-${inv.invite_id}`}
                    className="font-mono text-lg tracking-widest text-olive-bright hover:text-volt inline-flex items-center gap-1.5 transition-colors">
                    {inv.code} <Copy className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-sm font-body text-secondary-foreground truncate">{inv.label}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono text-xs uppercase text-muted-foreground">
                    {inv.used_count} / {inv.max_uses ?? "∞"} used · {timeAgo(inv.created_at)}
                  </span>
                  <button onClick={() => (sendingFor === inv.invite_id ? setSendingFor(null) : openSend(inv.invite_id))}
                    data-testid={`invite-send-${inv.invite_id}`} title="Email this invite"
                    className="text-olive-bright hover:text-volt transition-colors">
                    <Mail className="w-4 h-4" />
                  </button>
                  <button onClick={() => del(inv.invite_id)} data-testid={`invite-delete-${inv.invite_id}`} className="text-blaze hover:brightness-125 transition-all">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {sendingFor === inv.invite_id && (
                <div className="flex items-center gap-2 border-t border-sniper pt-3" data-testid={`invite-send-form-${inv.invite_id}`}>
                  <input type="email" value={sendEmail} onChange={(e) => setSendEmail(e.target.value)}
                    data-testid={`invite-send-email-${inv.invite_id}`} placeholder="operative@email.com" autoFocus
                    onKeyDown={(e) => e.key === "Enter" && sendInvite(inv.invite_id)}
                    className="flex-1 bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground text-sm transition-colors" />
                  <button onClick={() => sendInvite(inv.invite_id)} disabled={sendBusy} data-testid={`invite-send-submit-${inv.invite_id}`}
                    className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-sm px-4 py-2 transition-colors">
                    <Send className="w-3.5 h-3.5" /> {sendBusy ? "Sending" : "Send"}
                  </button>
                  <button onClick={() => setSendingFor(null)} data-testid={`invite-send-cancel-${inv.invite_id}`} className="text-muted-foreground hover:text-foreground p-2 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-3">
          <Users className="w-5 h-5 text-secondary-foreground" />
          <h2 className="font-heading text-2xl uppercase tracking-tight">Waitlist <span className="text-muted-foreground text-lg">({waitlist.length})</span></h2>
        </div>
        <div className="border border-sniper bg-surface divide-y divide-sniper" data-testid="waitlist">
          {waitlist.length === 0 && <p className="text-sm font-body text-muted-foreground p-6">No one on the waitlist yet.</p>}
          {waitlist.map((w) => (
            <div key={w.waitlist_id} className="px-4 py-2.5 flex items-center justify-between" data-testid={`waitlist-${w.waitlist_id}`}>
              <span className="font-mono text-sm text-foreground">{w.email}</span>
              <span className="font-mono text-[11px] uppercase text-muted-foreground">{timeAgo(w.created_at)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
